import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Clock, PlayCircle, MapPin, Calendar as CalIcon, X } from "lucide-react";

function TaskDrawer({ task, onClose, onUpdate }) {
  if (!task) return null;

  const setStatus = async (status) => {
    try {
      await api.patch(`/tasks/${task.id}/status`, { status });
      toast.success(`Marked as ${status.replace("_"," ")}`);
      onUpdate();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const options = [
    { v: "pending", label: "Pending", icon: Clock },
    { v: "in_progress", label: "In Progress", icon: PlayCircle },
    { v: "completed", label: "Completed", icon: CheckCircle2 },
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="task-drawer">
      <div className="w-full max-w-md h-full bg-[#0a0a0a] border-l border-[#262626] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[#262626] flex items-start justify-between">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Objective</p>
            <h3 className="font-display text-2xl text-white mt-1">{task.title}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-[#1c1c1c] text-gray-500"><X className="w-4 h-4"/></button>
        </div>

        <div className="p-6 space-y-6">
          {task.description && <p className="text-sm text-gray-300 leading-relaxed">{task.description}</p>}

          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm text-gray-400">
              <CalIcon className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5}/>
              <span className="font-mono">{task.scheduled_at.replace("T", " ").slice(0, 16)}</span>
            </div>
            {task.location && (
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <MapPin className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5}/>
                <span>{task.location}</span>
              </div>
            )}
          </div>

          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-3">Update status</p>
            <div className="grid grid-cols-3 gap-2">
              {options.map(o => {
                const Icon = o.icon;
                const active = task.status === o.v;
                return (
                  <button
                    key={o.v}
                    onClick={() => setStatus(o.v)}
                    className={`p-3 rounded-md border transition-colors text-center ${
                      active
                        ? "border-[#d4af37] bg-[#d4af37]/10"
                        : "border-[#262626] hover:border-[#3a3a3a]"
                    }`}
                    data-testid={`task-status-${o.v}`}
                  >
                    <Icon className={`w-4 h-4 mx-auto mb-1 ${active ? "text-[#d4af37]" : "text-gray-400"}`} strokeWidth={1.5}/>
                    <p className={`text-[10px] font-mono uppercase tracking-widest ${active ? "text-[#d4af37]" : "text-gray-400"}`}>
                      {o.label}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ExecutiveAgendaPage() {
  const [tasks, setTasks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get("/tasks");
      setTasks(data);
      if (selected) {
        const fresh = data.find(t => t.id === selected.id);
        if (fresh) setSelected(fresh);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); const id = setInterval(load, 10000); return () => clearInterval(id); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return tasks;
    return tasks.filter(t => t.status === filter);
  }, [tasks, filter]);

  const groupsByDate = useMemo(() => {
    const g = {};
    for (const t of filtered) {
      const d = (t.scheduled_at || "").slice(0, 10);
      (g[d] = g[d] || []).push(t);
    }
    return Object.entries(g).sort(([a],[b]) => a.localeCompare(b));
  }, [filtered]);

  const counts = {
    all: tasks.length,
    pending: tasks.filter(t => t.status === "pending").length,
    in_progress: tasks.filter(t => t.status === "in_progress").length,
    completed: tasks.filter(t => t.status === "completed").length,
  };

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-6 bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Marketing · Executive</p>
        <h1 className="font-display text-3xl text-white mt-1">My Agenda</h1>
      </div>

      <div className="px-10 py-8">
        <div className="grid grid-cols-4 gap-3 mb-8">
          {[
            { k: "all", label: "Total" },
            { k: "pending", label: "Pending" },
            { k: "in_progress", label: "In Progress" },
            { k: "completed", label: "Completed" },
          ].map(f => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              className={`iz-card p-4 text-left transition-colors ${filter === f.k ? "border-[#d4af37] bg-[#d4af37]/5" : "hover:border-[#3a3a3a]"}`}
              data-testid={`filter-${f.k}`}
            >
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500">{f.label}</p>
              <p className={`font-display text-3xl mt-1 ${filter === f.k ? "text-[#d4af37]" : "text-white"}`}>{counts[f.k]}</p>
            </button>
          ))}
        </div>

        {groupsByDate.length === 0 ? (
          <div className="iz-card p-12 text-center">
            <p className="text-gray-500 text-sm">No objectives {filter !== "all" ? `in "${filter.replace('_',' ')}"` : "yet"}.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {groupsByDate.map(([date, list]) => (
              <div key={date}>
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-3">
                  {new Date(date).toDateString()}
                </p>
                <div className="space-y-2">
                  {list.map(t => (
                    <button
                      key={t.id}
                      onClick={() => setSelected(t)}
                      className="w-full iz-card p-4 hover:border-[#d4af37]/60 transition-colors text-left"
                      data-testid={`agenda-task-${t.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex flex-col items-center min-w-[60px]">
                          <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">TIME</span>
                          <span className="font-display text-lg text-[#d4af37]">{(t.scheduled_at || "").slice(11, 16)}</span>
                        </div>
                        <div className="w-px h-10 bg-[#262626]"/>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm">{t.title}</p>
                          {t.location && (
                            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                              <MapPin className="w-3 h-3"/>{t.location}
                            </p>
                          )}
                        </div>
                        <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded ${
                          t.status === "completed" ? "text-[#10b981] bg-[#10b981]/10"
                          : t.status === "in_progress" ? "text-[#d4af37] bg-[#d4af37]/10"
                          : "text-gray-400 bg-[#1a1a1a]"
                        }`}>{t.status.replace("_"," ")}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <TaskDrawer task={selected} onClose={() => setSelected(null)} onUpdate={load}/>
    </div>
  );
}
